import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getFlujoCaja, exportarFlujoCajaDetallado, exportarFlujoVariaciones } from "@/lib/api";
import { formatCurrency, downloadBlob, parseBlobError } from "@/lib/utils";
import useModoAsiento, { CodigoCuenta } from "@/lib/useModoAsiento";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { 
  ChevronDown, 
  ChevronRight, 
  CheckCircle2, 
  AlertCircle,
  Info,
  TrendingUp,
  TrendingDown,
  Building2,
  Wallet,
  ArrowRightLeft,
  Wrench,
  FileSpreadsheet
} from "lucide-react";

export default function FlujoCajaContent({ ejercicioId }) {
  const navigate = useNavigate();
  // Navegación tipo SAP: con diario (HOLDED*), cada subcuenta salta a su mayor.
  const { modoAsiento, irAlMayor } = useModoAsiento(ejercicioId);
  const [flujoCaja, setFlujoCaja] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportandoDetallado, setExportandoDetallado] = useState(false);
  const [exportandoVariaciones, setExportandoVariaciones] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    cfo: true,
    cfi: true,
    cff: true,
    conciliacion: true
  });
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadFlujoCaja();
  }, [ejercicioId]);

  const loadFlujoCaja = async () => {
    setLoading(true);
    try {
      const response = await getFlujoCaja(ejercicioId);
      setFlujoCaja(response.data);
    } catch (error) {
      toast.error("Error al cargar flujo de caja");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExportarVariaciones = async () => {
    if (exportandoVariaciones) return;
    setExportandoVariaciones(true);
    try {
      const response = await exportarFlujoVariaciones(ejercicioId);
      downloadBlob(response.data, `FlujoVariaciones_${flujoCaja?.anio || ""}.xlsx`);
      toast.success("Variaciones exportadas correctamente");
    } catch (error) {
      toast.error(await parseBlobError(error, "No se pudo exportar las variaciones"));
    } finally {
      setExportandoVariaciones(false);
    }
  };

  const handleGuiaAction = async (paso) => {
    if (paso.accion_tipo === "exportar") {
      if (exportandoDetallado) return;
      setExportandoDetallado(true);
      try {
        const response = await exportarFlujoCajaDetallado(ejercicioId);
        downloadBlob(response.data, `FlujoCaja_Detallado_${flujoCaja?.anio || ""}.xlsx`);
        toast.success("Flujo de Caja Detallado exportado correctamente");
      } catch (error) {
        toast.error(await parseBlobError(error, "No se pudo exportar el Flujo de Caja Detallado"));
      } finally {
        setExportandoDetallado(false);
      }
    } else if (paso.accion_url) {
      navigate(paso.accion_url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (flujoCaja?.error) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-16">
          <AlertCircle className="h-12 w-12 text-amber-500 mb-4" />
          <h3 className="font-medium text-lg mb-2">No disponible</h3>
          <p className="text-muted-foreground text-center max-w-md">
            {flujoCaja.mensaje || "Se necesitan datos del ejercicio anterior para calcular el Estado de Flujos de Caja."}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { cfo, cfi, cff, cf_total, conciliacion } = flujoCaja || {};
  // P15: asientos "sin movimiento de caja" EXCLUIDOS de este EFE (nota de
  // transparencia — el balance sí los lleva).
  const sinCaja = flujoCaja?.operaciones_sin_caja || [];

  return (
    <div className="space-y-4" data-testid="flujo-caja-content">
      {/* Header con Check */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Ejercicio {flujoCaja?.anio}</h2>
          <p className="text-sm text-muted-foreground">vs ejercicio {flujoCaja?.anio_anterior}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => navigate(`/ejercicios/${ejercicioId}/efe`)}
            data-testid="ver-construccion-efe"
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Cómo se construye
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={handleExportarVariaciones}
            disabled={exportandoVariaciones}
            data-testid="exportar-variaciones"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {exportandoVariaciones ? "Exportando..." : "Exportar Excel"}
          </Button>
          <Badge
            variant="outline"
            className={conciliacion?.check_ok ? "text-emerald-600 border-emerald-600" : "text-rose-600 border-rose-600"}
          >
            {conciliacion?.check_ok ? (
              <><CheckCircle2 className="h-3 w-3 mr-1" /> Cuadra</>
            ) : (
              <><AlertCircle className="h-3 w-3 mr-1" /> Diferencia: {formatCurrency(conciliacion?.diferencia)}</>
            )}
          </Badge>
        </div>
      </div>

      {/* CFO - Cash Flow Operativo */}
      <FlujoCajaSection
        title="A) Cash Flow Operativo (CFO)"
        icon={TrendingUp}
        total={cfo?.total}
        expanded={expandedSections.cfo}
        onToggle={() => toggleSection("cfo")}
        color="blue"
      >
        <FlujoCajaLine label="Resultado del ejercicio" data={cfo?.resultado} onClick={() => setSelectedItem({...cfo?.resultado, label: "Resultado del ejercicio"})} />
        {/* F7 (oct-2026): método indirecto completo — amortización e impuesto
            diferido se ajustan DENTRO del flujo operativo. */}
        <FlujoCajaLine label="(+) Amortización (sin salida de caja)" data={cfo?.amortizacion} onClick={() => setSelectedItem({...cfo?.amortizacion, label: "Amortización"})} />
        <FlujoCajaLine label="(±) Impuesto diferido (474/479, sin caja)" data={cfo?.impuesto_diferido} onClick={() => setSelectedItem({...cfo?.impuesto_diferido, label: "Impuesto diferido"})} />
        <FlujoCajaLine label="Existencias (impacto caja)" data={cfo?.existencias} onClick={() => setSelectedItem({...cfo?.existencias, label: "Existencias"})} />
        <FlujoCajaLine label="Clientes (impacto caja)" data={cfo?.clientes} onClick={() => setSelectedItem({...cfo?.clientes, label: "Clientes"})} />
        <FlujoCajaLine label="Proveedores (impacto caja)" data={cfo?.proveedores} onClick={() => setSelectedItem({...cfo?.proveedores, label: "Proveedores"})} />
        <FlujoCajaLine label="Periodificaciones de activo (impacto caja)" data={cfo?.periodificaciones_activo} onClick={() => setSelectedItem({...cfo?.periodificaciones_activo, label: "Periodificaciones de activo"})} />
        <FlujoCajaLine label="Periodificaciones de pasivo (impacto caja)" data={cfo?.periodificaciones_pasivo} onClick={() => setSelectedItem({...cfo?.periodificaciones_pasivo, label: "Periodificaciones de pasivo"})} />
        <FlujoCajaLine label="Provisiones (impacto caja)" data={cfo?.provisiones} onClick={() => setSelectedItem({...cfo?.provisiones, label: "Provisiones"})} />
      </FlujoCajaSection>

      {/* CFI - Cash Flow Inversión */}
      <FlujoCajaSection
        title="B) Cash Flow Inversión (CFI)"
        icon={Building2}
        total={cfi?.total}
        expanded={expandedSections.cfi}
        onToggle={() => toggleSection("cfi")}
        color="amber"
      >
        <FlujoCajaLine label="Inmovilizado (intangible + material)" data={cfi?.inmovilizado} onClick={() => setSelectedItem({...cfi?.inmovilizado, label: "Inmovilizado"})} />
        <FlujoCajaLine label="Inversiones inmobiliarias" data={cfi?.inv_inmobiliarias} onClick={() => setSelectedItem({...cfi?.inv_inmobiliarias, label: "Inversiones inmobiliarias"})} />
        <FlujoCajaLine label="Empresas del grupo" data={cfi?.empresas_grupo} onClick={() => setSelectedItem({...cfi?.empresas_grupo, label: "Empresas del grupo"})} />
        <FlujoCajaLine label="Inversiones financieras" data={cfi?.inv_financieras} onClick={() => setSelectedItem({...cfi?.inv_financieras, label: "Inversiones financieras"})} />
      </FlujoCajaSection>

      {/* CFF - Cash Flow Financiero */}
      <FlujoCajaSection
        title="C) Cash Flow Financiero (CFF)"
        icon={Wallet}
        total={cff?.total}
        expanded={expandedSections.cff}
        onToggle={() => toggleSection("cff")}
        color="purple"
      >
        <FlujoCajaLine label="Patrimonio neto (ampliaciones/dividendos/subvenciones)" data={cff?.patrimonio} onClick={() => setSelectedItem({...cff?.patrimonio, label: "Patrimonio neto"})} />
        <FlujoCajaLine label="Deuda (nueva - amortizada)" data={cff?.deuda} onClick={() => setSelectedItem({...cff?.deuda, label: "Deuda"})} />
        <FlujoCajaLine label="Deudas con empresas del grupo" data={cff?.deuda_grupo} onClick={() => setSelectedItem({...cff?.deuda_grupo, label: "Deudas con empresas del grupo"})} />
      </FlujoCajaSection>

      {/* Cash Flow Total */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowRightLeft className="h-5 w-5 text-primary" />
              <span className="font-semibold text-lg">D) CASH FLOW TOTAL</span>
            </div>
            <span className={`font-mono font-bold text-xl ${cf_total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(cf_total)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Conciliación */}
      <FlujoCajaSection
        title="E) Conciliación (Check)"
        icon={CheckCircle2}
        total={null}
        expanded={expandedSections.conciliacion}
        onToggle={() => toggleSection("conciliacion")}
        color={conciliacion?.check_ok ? "emerald" : "rose"}
        showTotal={false}
      >
        <div className="space-y-2 px-4 py-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Tesorería inicial (año {flujoCaja?.anio_anterior})</span>
            <span className="font-mono">{formatCurrency(conciliacion?.saldo_inicial)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">+ Cash Flow Total</span>
            <span className={`font-mono ${conciliacion?.cf_total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {conciliacion?.cf_total >= 0 ? '+' : ''}{formatCurrency(conciliacion?.cf_total)}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between items-center font-medium">
            <span>= Tesorería final calculada</span>
            <span className="font-mono">{formatCurrency(conciliacion?.saldo_final_calculado)}</span>
          </div>
          <div className="flex justify-between items-center font-medium">
            <span>Tesorería según Balance</span>
            <span className="font-mono">{formatCurrency(conciliacion?.tesoreria_balance)}</span>
          </div>
          <Separator />
          <div className={`flex justify-between items-center p-2 rounded ${conciliacion?.check_ok ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-rose-50 dark:bg-rose-950'}`}>
            <span className="font-semibold">Diferencia</span>
            <span className={`font-mono font-bold ${conciliacion?.check_ok ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(conciliacion?.diferencia)}
            </span>
          </div>
          
          {!conciliacion?.check_ok && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm flex-1">
                  {conciliacion?.desglose_descuadre?.length > 0 ? (
                    <>
                      <p className="font-medium text-amber-800 dark:text-amber-200">Origen del descuadre (atribuido):</p>
                      <div className="mt-2 space-y-2">
                        {conciliacion.desglose_descuadre.map((d) => (
                          <div key={d.partida} className="border border-amber-200 dark:border-amber-800 rounded p-2">
                            <div className="flex justify-between items-center text-amber-800 dark:text-amber-200">
                              <span className="font-medium">{d.partida}</span>
                              <span className="font-mono font-bold">{d.efecto_caja >= 0 ? '+' : ''}{formatCurrency(d.efecto_caja)}</span>
                            </div>
                            {d.cuentas?.length > 0 && (
                              <ul className="mt-1 text-amber-700 dark:text-amber-300 space-y-0.5">
                                {d.cuentas.map((c) => (
                                  <li key={c.codigo} className="flex justify-between">
                                    <span>{c.codigo} · {c.nombre}</span>
                                    <span className="font-mono">Δ {formatCurrency(c.delta)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      Descuadre no atribuible a una partida concreta: revisa cuentas sin clasificar o reclasificaciones entre años.
                    </p>
                  )}
                  {conciliacion?.avisos?.length > 0 && (
                    <ul className="mt-2 text-amber-700 dark:text-amber-300 list-disc list-inside space-y-1">
                      {conciliacion.avisos.map((a, i) => (<li key={i}>{a}</li>))}
                    </ul>
                  )}
                  {conciliacion?.guia?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-amber-200 dark:border-amber-800" data-testid="guia-descuadre">
                      <p className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
                        <Wrench className="h-4 w-4" />
                        ¿Cómo lo arreglo?
                      </p>
                      <ol className="mt-3 space-y-3">
                        {conciliacion.guia.map((paso, i) => (
                          <li key={`${paso.tipo}-${i}`} className="flex gap-2.5">
                            <span className="flex-none flex items-center justify-center h-5 w-5 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-xs font-bold mt-0.5">
                              {i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-amber-800 dark:text-amber-200">{paso.titulo}</p>
                              <p className="mt-0.5 text-amber-700 dark:text-amber-300">{paso.descripcion}</p>
                              {(paso.accion_url || paso.accion_tipo === "exportar") && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2 h-7 text-xs border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900"
                                  disabled={paso.accion_tipo === "exportar" && exportandoDetallado}
                                  onClick={() => handleGuiaAction(paso)}
                                  data-testid={`guia-accion-${paso.tipo}`}
                                >
                                  {paso.accion_tipo === "exportar" && exportandoDetallado
                                    ? "Exportando..."
                                    : paso.accion_label}
                                </Button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </FlujoCajaSection>

      {/* P15: nota de transparencia — operaciones sin movimiento de caja
          excluidas de este EFE (el balance sí las lleva). */}
      {sinCaja.length > 0 && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-3 text-sm"
             data-testid="efe-sin-caja">
          <p className="font-medium text-amber-800 dark:text-amber-200">
            Operaciones sin movimiento de caja (excluidas del estado de flujos)
          </p>
          <ul className="mt-1.5 space-y-0.5 text-amber-700 dark:text-amber-300">
            {sinCaja.map((op, i) => (
              <li key={i} className="flex justify-between gap-4">
                <span>{op.concepto}{op.fecha ? ` (${op.fecha})` : ""}</span>
                <span className="font-mono tabular-nums">{formatCurrency(op.importe)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-amber-700/80 dark:text-amber-300/80">
            Estos asientos modifican el balance (inmovilizado, deuda…) pero no mueven
            tesorería: inversión y financiación se muestran sin ellos.
          </p>
        </div>
      )}

      {/* Modal de Drill-down */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              {selectedItem?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Valor */}
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Valor</p>
              <p className={`text-2xl font-mono font-bold ${selectedItem?.valor >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {formatCurrency(selectedItem?.valor)}
              </p>
            </div>

            {/* Fórmula */}
            {selectedItem?.formula && (
              <div>
                <p className="text-sm font-medium mb-2">Fórmula</p>
                <code className="block p-3 bg-muted rounded text-sm">
                  {selectedItem.formula}
                </code>
              </div>
            )}

            {/* Explicación */}
            {selectedItem?.explicacion && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 {selectedItem.explicacion}
                </p>
              </div>
            )}

            {/* Detalle de inputs */}
            {selectedItem?.detalle && (
              <div>
                <p className="text-sm font-medium mb-2">Datos utilizados</p>
                <div className="border rounded-lg divide-y">
                  {Object.entries(selectedItem.detalle).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center px-3 py-2 text-sm">
                      <span className="text-muted-foreground">{formatDetailKey(key)}</span>
                      <span className="font-mono">{formatCurrency(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Desglose por subcuenta */}
            {selectedItem?.subcuentas?.length > 0 && (
              <SubcuentasDesglose
                subcuentas={selectedItem.subcuentas}
                anio={flujoCaja?.anio}
                anioAnterior={flujoCaja?.anio_anterior}
                onExport={handleExportarVariaciones}
                exportando={exportandoVariaciones}
                onVerMayor={modoAsiento ? irAlMayor : null}
                impactoCaja={selectedItem.valor}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SubcuentasDesglose({ subcuentas, anio, anioAnterior, onExport, exportando, onVerMayor, impactoCaja }) {
  const totalDelta = subcuentas.reduce((acc, c) => acc + (c.delta || 0), 0);
  // Con las filas "Resto"/ajuste del backend, |totalDelta| == |impactoCaja|:
  // el desglose siempre suma la línea (solo puede diferir el signo de la fórmula).

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium">Desglose por subcuenta</p>
        {onExport && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 text-xs"
            onClick={onExport}
            disabled={exportando}
            data-testid="exportar-variaciones-modal"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {exportando ? "Exportando..." : "Exportar Excel"}
          </Button>
        )}
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted text-xs text-muted-foreground border-b">
              <th className="text-left px-3 py-2 font-semibold">Código</th>
              <th className="text-left px-3 py-2 font-semibold">Nombre</th>
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Saldo {anioAnterior ?? "t-1"}</th>
              <th className="text-right px-3 py-2 font-semibold whitespace-nowrap">Saldo {anio ?? "t"}</th>
              <th className="text-right px-3 py-2 font-semibold">Δ</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {subcuentas.map((c) => (
              <tr key={c.codigo} className={c.es_resto || c.es_ajuste ? "bg-muted/20" : "hover:bg-muted/40"}>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap align-top">
                  {c.es_resto || c.es_ajuste ? (
                    <span>{c.codigo}</span>
                  ) : (
                    <CodigoCuenta codigo={c.codigo} onVerMayor={onVerMayor} />
                  )}
                </td>
                <td className={`px-3 py-2 align-top ${c.es_resto || c.es_ajuste ? "italic text-muted-foreground" : ""}`}>{c.nombre}</td>
                <td className="text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap align-top">{formatCurrency(c.saldo_t1)}</td>
                <td className="text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap align-top">{formatCurrency(c.saldo_t)}</td>
                <td className={`text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap align-top font-medium ${c.delta > 0 ? 'text-emerald-600' : c.delta < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                  {c.delta > 0 ? '+' : ''}{formatCurrency(c.delta)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t bg-muted/50 font-semibold">
              <td className="px-3 py-2" colSpan={4}>Variación total de balance</td>
              <td className={`text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap ${totalDelta > 0 ? 'text-emerald-600' : totalDelta < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                {totalDelta > 0 ? '+' : ''}{formatCurrency(totalDelta)}
              </td>
            </tr>
            {typeof impactoCaja === "number" && (
              <tr className="bg-muted/50 font-semibold">
                <td className="px-3 py-2" colSpan={4}>= Impacto en caja de este apunte</td>
                <td className={`text-right px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap ${impactoCaja > 0 ? 'text-emerald-600' : impactoCaja < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                  {impactoCaja > 0 ? '+' : ''}{formatCurrency(impactoCaja)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function FlujoCajaSection({ title, icon: Icon, total, expanded, onToggle, children, color, showTotal = true }) {
  const colorClasses = {
    blue: "text-blue-600 bg-blue-50 dark:bg-blue-950",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-950",
    purple: "text-purple-600 bg-purple-50 dark:bg-purple-950",
    emerald: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950",
    rose: "text-rose-600 bg-rose-50 dark:bg-rose-950"
  };

  return (
    <Card>
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <CardTitle className="font-semibold">{title}</CardTitle>
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
              {showTotal && (
                <span className={`font-mono font-bold ${total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {formatCurrency(total)}
                </span>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            <div className="space-y-1">
              {children}
            </div>
            {showTotal && (
              <>
                <Separator className="my-3" />
                <div className="flex justify-between items-center px-4 py-2 bg-muted/50 rounded">
                  <span className="font-medium">Total</span>
                  <span className={`font-mono font-bold ${total >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(total)}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function FlujoCajaLine({ label, data, onClick }) {
  const valor = data?.valor ?? 0;
  const hasDetail = data?.formula || data?.detalle;

  return (
    <div
      onClick={hasDetail ? onClick : undefined}
      className={`flex justify-between items-center px-4 py-2 rounded ${hasDetail ? 'cursor-pointer hover:bg-muted/50' : ''}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm">{label}</span>
        {hasDetail && <Info className="h-3 w-3 text-muted-foreground" />}
      </div>
      <span className={`font-mono text-sm ${valor > 0 ? 'text-emerald-600' : valor < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
        {formatCurrency(valor)}
      </span>
    </div>
  );
}

function formatDetailKey(key) {
  // Convertir snake_case a texto legible
  return key
    .replace(/_t1$/, ' (año t-1)')
    .replace(/_t$/, ' (año t)')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}
